
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './components/ProductCard';
import styles from './ProductCarousel.module.css';
import { getCelebritiesNoAuth } from '../../../../services/celebrityService';
import { AuthContext } from '../../../../context/AuthContext';
import LoginModal from '../../../../auth/login';

const ProductCarousel = () => {
    const [celebrities, setCelebrities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [selectedCelebrity, setSelectedCelebrity] = useState(null);

    const carouselTrackRef = useRef(null);

    const { isAuthenticated, user } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        loadCelebrities();
    }, [user, isAuthenticated]);

    const scrollCarousel = (direction) => {
        const track = carouselTrackRef.current;
        if (!track) return;

        const scrollAmount = 300;
        const currentScroll = track.scrollLeft;

        if (direction === 'next') {
            track.scrollTo({
                left: currentScroll + scrollAmount,
                behavior: 'smooth'
            });
        } else if (direction === 'prev') {
            track.scrollTo({
                left: currentScroll - scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const loadCelebrities = async () => {
        try {
            setLoading(true);
            const response = await getCelebritiesNoAuth(1, 14);

            if (response.message === "Celebrities retrieved successfully") {
                // Get current user info
                const currentUserId = user?.userId || user?._id || user?.id;
                const currentUserName = user?.fullName;
                const currentUserPhone = user?.userPhone;
                const currentUserEmail = user?.email;

                console.log('🔍 FILTERING DEBUG:');
                console.log('User ID:', currentUserId);
                console.log('User Name:', currentUserName);
                console.log('User Phone:', currentUserPhone);
                console.log('User Type:', user?.userType);

                // Filter celebrities - STRICT FILTERING
                let filteredData = response.data;

                if (isAuthenticated && user?.userType === 'celeb') {
                    console.log('🎯 Applying strict filtering for celeb user...');
                    console.log('Total celebs before filtering:', filteredData.length);

                    filteredData = response.data.filter(celeb => {
                        // Check multiple criteria for self-identification
                        const isSameId = celeb._id === currentUserId;
                        const isSameName = currentUserName &&
                            celeb.displayName?.toLowerCase() === currentUserName.toLowerCase();
                        const isSamePhone = currentUserPhone &&
                            celeb.userPhone === currentUserPhone;
                        const isSameEmail = currentUserEmail && celeb.email &&
                            celeb.email.toLowerCase() === currentUserEmail.toLowerCase();

                        const isSelf = isSameId || isSameName || isSamePhone || isSameEmail;

                        if (isSelf) {
                            console.log(`❌ EXCLUDING SELF: ${celeb.displayName} (ID: ${celeb._id})`);
                            console.log(`   ID match: ${isSameId}, Name match: ${isSameName}, Phone match: ${isSamePhone}, Email match: ${isSameEmail}`);
                        }

                        return !isSelf;
                    });

                    console.log('✅ Celebs after filtering:', filteredData.length);
                } else {
                    console.log('👤 Not a celeb user, showing all celebrities');
                }

                const transformedCelebs = filteredData.map((celeb) => {
                    let imageUrl = celeb.picture;
                    if (!imageUrl || (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/'))) {
                        imageUrl = 'https://c.animaapp.com/mhof1dcsmYxOSQ/assets/53eacf_65bf99eb44014e57bd92852f62be5d3f~mv2.jpg';
                    }

                    const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
                    let shoutoutPrice = 100.00;

                    if (hasShoutoutPricing) {
                        const minPriceShoutout = celeb.shoutout.reduce((min, current) => {
                            return current.price < min.price ? current : min;
                        }, celeb.shoutout[0]);
                        shoutoutPrice = parseFloat(minPriceShoutout.price);
                    }

                    const category = celeb.celebType || 'Popular';

                    return {
                        id: celeb._id,
                        ariaLabel: `${celeb.displayName}. ${category} gallery`,
                        productUrl: "#",
                        imageUrl: imageUrl,
                        imageAlt: celeb.displayName,
                        categoryLabel: category,
                        productName: celeb.displayName,
                        price: `$${shoutoutPrice.toFixed(2)}`,
                        hasPricing: hasShoutoutPricing,
                        rawPrice: shoutoutPrice,
                        originalData: celeb
                    };
                });

                setCelebrities(transformedCelebs);
            } else {
                setError(response.message || 'Failed to load celebrities');
            }
        } catch (err) {
            console.error('Error loading celebrities:', err);
            setError(err.message || 'Failed to load celebrities');
        } finally {
            setLoading(false);
        }
    };

    const handleCelebrityClick = (celebrity) => {
        // Get current user info for comparison
        const currentUserId = user?.userId || user?._id || user?.id;
        const currentUserName = user?.fullName;
        const currentUserPhone = user?.userPhone;
        const currentUserEmail = user?.email;

        // STRICT SELF-CHECK
        const isSameId = celebrity.id === currentUserId;
        const isSameName = currentUserName &&
            celebrity.productName?.toLowerCase() === currentUserName.toLowerCase();
        const isSamePhone = currentUserPhone &&
            celebrity.originalData?.userPhone === currentUserPhone;
        const isSameEmail = currentUserEmail && celebrity.originalData?.email &&
            celebrity.originalData.email.toLowerCase() === currentUserEmail.toLowerCase();

        const isSelf = isSameId || isSameName || isSamePhone || isSameEmail;

        console.log('🖱️ CLICK DEBUG:', {
            clickedCeleb: celebrity.productName,
            clickedId: celebrity.id,
            currentUserId: currentUserId,
            currentUserName: currentUserName,
            isSameId: isSameId,
            isSameName: isSameName,
            isSamePhone: isSamePhone,
            isSameEmail: isSameEmail,
            isSelf: isSelf
        });

        // Prevent self-booking with multiple checks
        if (isAuthenticated && user?.userType === 'celeb' && isSelf) {
            alert("🚫 You cannot request a shoutout from yourself!");
            console.log('🛑 SELF-BOOKING PREVENTED!');
            return;
        }

        if (!isAuthenticated) {
            localStorage.setItem('pendingBooking', JSON.stringify({
                celebId: celebrity.id,
                name: celebrity.productName,
                category: celebrity.categoryLabel,
                image: celebrity.imageUrl,
                price: celebrity.rawPrice,
                shoutoutPricing: celebrity.originalData.shoutout || []
            }));

            setSelectedCelebrity(celebrity);
            setShowLoginModal(true);
            return;
        }

        if (!celebrity.hasPricing) {
            alert('This celebrity has not set up their pricing yet.');
            return;
        }

        const bookingParams = new URLSearchParams({
            celebId: celebrity.id,
            name: celebrity.productName,
            category: celebrity.categoryLabel,
            image: celebrity.imageUrl,
            price: celebrity.rawPrice,
            displayName: celebrity.productName,
            celebType: celebrity.categoryLabel,
            shoutoutPricing: JSON.stringify(celebrity.originalData.shoutout || [])
        });

        navigate(`/booking?${bookingParams.toString()}`);
    };

    // Check if celebrity is the current user (for UI display)
    const isSelfProfile = (celebrity) => {
        if (!isAuthenticated || user?.userType !== 'celeb') return false;

        const currentUserId = user?.userId || user?._id || user?.id;
        const currentUserName = user?.fullName;

        const isSameId = celebrity.id === currentUserId;
        const isSameName = currentUserName &&
            celebrity.productName?.toLowerCase() === currentUserName.toLowerCase();

        return isSameId || isSameName;
    };

    if (loading) {
        return (
            <section className={styles.productCarousel}>
                <div className={styles.backgroundOverlay}>
                    <div className={styles.backgroundBlack}></div>
                </div>
                <div className={styles.carouselContainer}>
                    <div className={styles.carouselContent}>
                        <div className={styles.loadingState}>
                            <p>Loading celebrities...</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className={styles.productCarousel}>
                <div className={styles.backgroundOverlay}>
                    <div className={styles.backgroundBlack}></div>
                </div>
                <div className={styles.carouselContainer}>
                    <div className={styles.carouselContent}>
                        <div className={styles.errorState}>
                            <p>Error loading celebrities: {error}</p>
                            <button onClick={loadCelebrities}>Try Again</button>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className={styles.productCarousel}>
                <div className={styles.backgroundOverlay}>
                    <div className={styles.backgroundBlack}></div>
                </div>

                <div className={styles.carouselContainer}>
                    <div className={styles.carouselContent}>
                        <div className={styles.prevButton}>
                            <button
                                aria-label="Previous Product"
                                type="button"
                                onClick={() => scrollCarousel('prev')}
                            >
                                <img
                                    src="https://c.animaapp.com/mhof1dcsmYxOSQ/assets/icon-13.svg"
                                    alt="Previous"
                                    style={{ transform: 'rotate(180deg)' }}
                                />
                            </button>
                        </div>

                        <div
                            className={styles.carouselTrack}
                            ref={carouselTrackRef}
                        >
                            {celebrities.map((celebrity) => {
                                const isSelf = isSelfProfile(celebrity);

                                return (
                                    <div
                                        key={celebrity.id}
                                        className={styles.productWrapper}
                                        onClick={() => handleCelebrityClick(celebrity)}
                                        style={{
                                            cursor: isSelf ? 'not-allowed' : 'pointer',
                                            position: 'relative',
                                            opacity: isSelf ? 0.7 : 1
                                        }}
                                    >
                                        {isSelf && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                background: '#dc3545',
                                                color: 'white',
                                                padding: '6px 10px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                zIndex: '100',
                                                pointerEvents: 'none',
                                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                                            }}>
                                                YOU
                                            </div>
                                        )}
                                        <ProductCard {...celebrity} />
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.nextButton}>
                            <button
                                aria-label="Next Product"
                                type="button"
                                onClick={() => scrollCarousel('next')}
                            >
                                <img
                                    src="https://c.animaapp.com/mhof1dcsmYxOSQ/assets/icon-13.svg"
                                    alt="Next"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <LoginModal
                show={showLoginModal}
                onHide={() => {
                    setShowLoginModal(false);
                    setSelectedCelebrity(null);
                }}
            />
        </>
    );
};

export default ProductCarousel;

// import React, { useState, useEffect, useContext, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import ProductCard from './components/ProductCard';
// import styles from './ProductCarousel.module.css';
// import { getCelebritiesNoAuth } from '../../../../services/celebrityService';
// import { AuthContext } from '../../../../context/AuthContext';
// import LoginModal from '../../../../auth/login';

// const ProductCarousel = () => {
//     const [celebrities, setCelebrities] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [showLoginModal, setShowLoginModal] = useState(false);
//     const [selectedCelebrity, setSelectedCelebrity] = useState(null);

//     const carouselTrackRef = useRef(null);

//     const { isAuthenticated } = useContext(AuthContext);
//     const navigate = useNavigate();

//     useEffect(() => {
//         loadCelebrities();
//     }, []);

//     const scrollCarousel = (direction) => {
//         const track = carouselTrackRef.current;
//         if (!track) return;

//         const scrollAmount = 300; // Adjust this value to control how much it scrolls
//         const currentScroll = track.scrollLeft;

//         if (direction === 'next') {
//             track.scrollTo({
//                 left: currentScroll + scrollAmount,
//                 behavior: 'smooth'
//             });
//         } else if (direction === 'prev') {
//             track.scrollTo({
//                 left: currentScroll - scrollAmount,
//                 behavior: 'smooth'
//             });
//         }
//     };

//     const loadCelebrities = async () => {
//         try {
//             setLoading(true);
//             const response = await getCelebritiesNoAuth(1, 14);

//             if (response.message === "Celebrities retrieved successfully") {
//                 const transformedCelebs = response.data.map((celeb) => {
//                     let imageUrl = celeb.picture;
//                     if (!imageUrl || (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/'))) {
//                         imageUrl = 'https://c.animaapp.com/mhof1dcsmYxOSQ/assets/53eacf_65bf99eb44014e57bd92852f62be5d3f~mv2.jpg';
//                     }

//                     const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
//                     let shoutoutPrice = 100.00;

//                     if (hasShoutoutPricing) {
//                         const minPriceShoutout = celeb.shoutout.reduce((min, current) => {
//                             return current.price < min.price ? current : min;
//                         }, celeb.shoutout[0]);
//                         shoutoutPrice = parseFloat(minPriceShoutout.price);
//                     }

//                     const category = celeb.celebType || 'Popular';

//                     return {
//                         id: celeb._id,
//                         ariaLabel: `${celeb.displayName}. ${category} gallery`,
//                         productUrl: "#",
//                         imageUrl: imageUrl,
//                         imageAlt: celeb.displayName,
//                         categoryLabel: category,
//                         productName: celeb.displayName,
//                         price: `$${shoutoutPrice.toFixed(2)}`,
//                         hasPricing: hasShoutoutPricing,
//                         rawPrice: shoutoutPrice,
//                         // Include all original data for booking page
//                         originalData: celeb
//                     };
//                 });

//                 setCelebrities(transformedCelebs);
//             } else {
//                 setError(response.message || 'Failed to load celebrities');
//             }
//         } catch (err) {
//             console.error('Error loading celebrities:', err);
//             setError(err.message || 'Failed to load celebrities');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleCelebrityClick = (celebrity) => {
//         if (!isAuthenticated) {
//             // Store celebrity data in localStorage for after login
//             localStorage.setItem('pendingBooking', JSON.stringify({
//                 celebId: celebrity.id,
//                 name: celebrity.productName,
//                 category: celebrity.categoryLabel,
//                 image: celebrity.imageUrl,
//                 price: celebrity.rawPrice,
//                 shoutoutPricing: celebrity.originalData.shoutout || []
//             }));

//             setSelectedCelebrity(celebrity);
//             setShowLoginModal(true);
//             return;
//         }

//         if (!celebrity.hasPricing) {
//             alert('This celebrity has not set up their pricing yet.');
//             return;
//         }

//         // Navigate to booking page with ALL celebrity details including shoutout pricing
//         const bookingParams = new URLSearchParams({
//             celebId: celebrity.id,
//             name: celebrity.productName,
//             category: celebrity.categoryLabel,
//             image: celebrity.imageUrl,
//             price: celebrity.rawPrice,
//             displayName: celebrity.productName,
//             celebType: celebrity.categoryLabel,
//             shoutoutPricing: JSON.stringify(celebrity.originalData.shoutout || [])
//         });

//         navigate(`/booking?${bookingParams.toString()}`);
//     };

//     if (loading) {
//         return (
//             <section className={styles.productCarousel}>
//                 <div className={styles.backgroundOverlay}>
//                     <div className={styles.backgroundBlack}></div>
//                 </div>
//                 <div className={styles.carouselContainer}>
//                     <div className={styles.carouselContent}>
//                         <div className={styles.loadingState}>
//                             <p>Loading celebrities...</p>
//                         </div>
//                     </div>
//                 </div>
//             </section>
//         );
//     }

//     if (error) {
//         return (
//             <section className={styles.productCarousel}>
//                 <div className={styles.backgroundOverlay}>
//                     <div className={styles.backgroundBlack}></div>
//                 </div>
//                 <div className={styles.carouselContainer}>
//                     <div className={styles.carouselContent}>
//                         <div className={styles.errorState}>
//                             <p>Error loading celebrities: {error}</p>
//                             <button onClick={loadCelebrities}>Try Again</button>
//                         </div>
//                     </div>
//                 </div>
//             </section>
//         );
//     }

//     return (
//         <>
//             <section className={styles.productCarousel}>
//                 <div className={styles.backgroundOverlay}>
//                     <div className={styles.backgroundBlack}></div>
//                 </div>

//                 <div className={styles.carouselContainer}>
//                     <div className={styles.carouselContent}>
//                         {/* Previous Button - ADD THIS */}
//                         <div className={styles.prevButton}>
//                             <button
//                                 aria-label="Previous Product"
//                                 type="button"
//                                 onClick={() => scrollCarousel('prev')}
//                             >
//                                 <img
//                                     src="https://c.animaapp.com/mhof1dcsmYxOSQ/assets/icon-13.svg"
//                                     alt="Previous"
//                                     style={{ transform: 'rotate(180deg)' }}
//                                 />
//                             </button>
//                         </div>

//                         {/* Carousel Track */}
//                         <div
//                             className={styles.carouselTrack}
//                             ref={carouselTrackRef}
//                         >
//                             {celebrities.map((celebrity) => (
//                                 <div
//                                     key={celebrity.id}
//                                     className={styles.productWrapper}
//                                     onClick={() => handleCelebrityClick(celebrity)}
//                                     style={{ cursor: 'pointer' }}
//                                 >
//                                     <ProductCard {...celebrity} />
//                                 </div>
//                             ))}
//                         </div>

//                         {/* Next Button */}
//                         <div className={styles.nextButton}>
//                             <button
//                                 aria-label="Next Product"
//                                 type="button"
//                                 onClick={() => scrollCarousel('next')}
//                             >
//                                 <img
//                                     src="https://c.animaapp.com/mhof1dcsmYxOSQ/assets/icon-13.svg"
//                                     alt="Next"
//                                 />
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             <LoginModal
//                 show={showLoginModal}
//                 onHide={() => {
//                     setShowLoginModal(false);
//                     setSelectedCelebrity(null);
//                 }}
//             />
//         </>
//     );
// };

// export default ProductCarousel;
